import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/context/AuthContext';
import { BottomTabNav } from '@/components/BottomTabNav';
import { RequireAuth } from '@/components/RequireAuth';
import { FullPageLoader } from '@/components/Loaders';
import { TimeOfDayBackground } from '@/components/TimeOfDayBackground';
import '@/App.css';

// Pages
import SplashPage from '@/pages/SplashPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import HomePage from '@/pages/HomePage';
import DiscoverPage from '@/pages/DiscoverPage';
import CategoryPage from '@/pages/CategoryPage';
import VenueDetailPage from '@/pages/VenueDetailPage';
import USAMapPage from '@/pages/USAMapPage';
import ScanPage from '@/pages/ScanPage';
import ProfilePage from '@/pages/ProfilePage';
import MyQRPage from '@/pages/MyQRPage';
import AdminPage from '@/pages/AdminPage';
import ContactPage from '@/pages/ContactPage';
import GoLivePage from '@/pages/GoLivePage';
import PricingPage from '@/pages/PricingPage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import ClaimVenuePage from '@/pages/ClaimVenuePage';
import NotFoundPage from '@/pages/NotFoundPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';
import StorySharePage from '@/pages/StorySharePage';
import UserProfilePage from '@/pages/UserProfilePage';
import { WelcomeTour } from '@/components/WelcomeTour';

// Layout for authenticated tab-based pages (bottom nav visible)
function AppShell() {
  return (
    <div className="App relative min-h-screen pb-28">
      <TimeOfDayBackground intensity={0.62} />
      <div className="relative z-10">
        <Suspense fallback={<FullPageLoader />}>
          <Outlet />
        </Suspense>
      </div>
      <BottomTabNav />
      <WelcomeTour />
    </div>
  );
}

// Layout without bottom nav (auth, splash, modals)
function BareShell() {
  return (
    <div className="App relative min-h-screen">
      <TimeOfDayBackground intensity={0.62} />
      <div className="relative z-10">
        <Suspense fallback={<FullPageLoader />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
}

export default function App() {
  // Force dark theme
  if (typeof document !== 'undefined') {
    document.documentElement.classList.add('dark');
  }
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-center"
            theme="dark"
            richColors
            toastOptions={{
              classNames: {
                toast: 'bg-black/85 border border-white/10 backdrop-blur-xl text-white',
              },
            }}
          />
          <Routes>
            {/* Splash + Auth - no bottom nav */}
            <Route element={<BareShell />}>
              <Route path="/welcome" element={<SplashPage />} />
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/signup" element={<SignupPage />} />
              <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              <Route path="/story/:storyId" element={<StorySharePage />} />
            </Route>

            {/* Authenticated tabs */}
            <Route element={<AppShell />}>
              <Route path="/" element={<RequireAuth><HomePage /></RequireAuth>} />
              <Route path="/discover" element={<RequireAuth><DiscoverPage /></RequireAuth>} />
              <Route path="/discover/:category" element={<RequireAuth><CategoryPage /></RequireAuth>} />
              <Route path="/venue/:venueId" element={<RequireAuth><VenueDetailPage /></RequireAuth>} />
              <Route path="/user/:userId" element={<RequireAuth><UserProfilePage /></RequireAuth>} />
              <Route path="/map" element={<RequireAuth><USAMapPage /></RequireAuth>} />
              <Route path="/scan" element={<RequireAuth><ScanPage /></RequireAuth>} />
              <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
              <Route path="/profile/qr" element={<RequireAuth><MyQRPage /></RequireAuth>} />
              <Route path="/profile/go-live" element={<RequireAuth><GoLivePage /></RequireAuth>} />
              <Route path="/profile/subscription" element={<RequireAuth><SubscriptionPage /></RequireAuth>} />
              <Route path="/profile/claim-venue" element={<RequireAuth><ClaimVenuePage /></RequireAuth>} />
              <Route path="/admin" element={<RequireAuth role="admin"><AdminPage /></RequireAuth>} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
